import { ReactNode, useCallback } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Link } from 'react-router-dom';

import { useRecoilState } from 'recoil';

import { showingDockedCopilotState, useIsCopilotEnabled } from 'app/AppState';
import { useIsAdmin, useSignOut, useUserDisplayName } from 'auth';
import { Avatar } from 'components/avatar';
import { Badge } from 'components/badge';
import { IconButton, IconButtonLink } from 'components/button';
import { Divider } from 'components/divider';
import { Flex } from 'components/flex';
import { Icon } from 'components/icon';
import { ReactComponent as Bell } from 'components/icon/lindua/bell2.svg';
import { ReactComponent as Compass } from 'components/icon/lindua/compass3.svg';
import { ReactComponent as Anvil } from 'components/icon/lindua/equalizer2.svg';
import { ReactComponent as MagicWand } from 'components/icon/lindua/magic-wand.svg';
import { Logo } from 'components/logo';
import { Menu, MenuItem, MenuLink, MenuList, MenuTitle, MenuTrigger } from 'components/menu';
import { Text } from 'components/text';
import { Tooltip, TooltipProps } from 'components/tooltip';
import { useNotifications } from 'data/notifications';
import { usePreferenceState } from 'data/preferences';
import { useAppVersionQuery } from 'data/settings';
import { styled, useTheme } from 'stitches';

import { CopilotViewModePreference } from './PreferencesDialog';

const Navbar = styled('nav', {
  backgroundColor: '$navbar-bg',
  color: '$navbar-text',

  justifyContent: 'space-between',
  flexShrink: 0,
  display: 'flex',

  variants: {
    orientation: {
      vertical: {
        flexDirection: 'column',
        padding: '$5 0',
        width: '4rem',
      },
      horizontal: {
        flexDirection: 'row',
        padding: '0 $5',
        height: '4rem',
      },
    },
  },
});

const NavbarLogoContainer = styled('div', {
  p: '$1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    // Ensure the tooltips align with the edge of the navbar
    navBarOrientation: {
      vertical: {
        minWidth: '40px',
      },
      horizontal: {
        minHeight: '40px',
      },
    },
  },
});

const NavbarAvatarAnchor = styled('div', {
  pointerEvents: 'none',
});

const NavbarAvatarContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  pointerEvents: 'all',
  flexShrink: 0,

  variants: {
    // Ensure the tooltips align with the edge of the navbar
    navBarOrientation: {
      vertical: {
        minWidth: '40px',
      },
      horizontal: {
        minHeight: '40px',
      },
    },
  },
});

type GlobalNavProps = {
  children?: ReactNode;
  orientation: 'horizontal' | 'vertical';
  onDrawerSelect?(drawer: string): void;
  onPreferences?(): void;
};

export function GlobalNav(props: GlobalNavProps) {
  const { children, orientation, onDrawerSelect, onPreferences } = props;

  const theme = useTheme();
  const signOut = useSignOut();
  const userDisplayName = useUserDisplayName();
  const isAdmin = useIsAdmin();
  const { unreadCount } = useNotifications();
  const { appVersion } = useAppVersionQuery();
  const isCopilotEnabled = useIsCopilotEnabled();

  useHotkeys('ctrl+q', () => onDrawerSelect?.('quickNavigation'));

  const [copilotViewMode] = usePreferenceState<CopilotViewModePreference>({
    key: 'copilotViewMode',
    defaultValue: 'dock',
  });
  const [showDockedCopilot, setShowDockedCopilot] = useRecoilState(showingDockedCopilotState);

  const onCopilotClicked = useCallback(() => {
    if (copilotViewMode === 'dock') {
      setShowDockedCopilot(!showDockedCopilot);
    } else {
      window.open(
        `/${process.env.REACT_APP_COPILOT_PATH_SEGMENT}`,
        'decisio-copilot',
        copilotViewMode === 'popup'
          ? 'width=600,height=400,menubar=no,toolbar=no,location=no,status=no,titlebar=no,scrollbars=no'
          : undefined
      );
    }
  }, [copilotViewMode, showDockedCopilot, setShowDockedCopilot]);

  const flexDirection = orientation === 'vertical' ? 'column' : 'row';

  return (
    <Navbar orientation={orientation}>
      <Flex direction={flexDirection} align="center" gap="5">
        <NavbarTooltip title="Decisio">
          <NavbarLogoContainer navBarOrientation={orientation}>
            <Link to="/">
              <Logo url={(theme as any)?.logo.uri?.value} size="$8" />
            </Link>
          </NavbarLogoContainer>
        </NavbarTooltip>
        <NavbarTooltip title="Quick navigation (Ctrl + Q)">
          <IconButton
            aria-label="Quick navigation"
            onClick={() => onDrawerSelect?.('quickNavigation')}
          >
            <Icon svg={<Compass />} />
          </IconButton>
        </NavbarTooltip>
      </Flex>

      <Flex direction={flexDirection} align="center" gap="5">
        {isCopilotEnabled && (
          <NavbarTooltip title="Dek">
            <IconButton aria-label="Dek" onClick={onCopilotClicked}>
              <Icon svg={<MagicWand />} />
            </IconButton>
          </NavbarTooltip>
        )}

        {isAdmin && (
          <NavbarTooltip title="Administration">
            <IconButtonLink to="/admin" aria-label="Admin">
              <Icon svg={<Anvil />} />
            </IconButtonLink>
          </NavbarTooltip>
        )}

        <Badge badgeContent={unreadCount} colorScheme="danger">
          <NavbarTooltip title="Notifications">
            <IconButton
              aria-label="Notifications"
              onClick={() => onDrawerSelect?.('notifications')}
            >
              <Icon svg={<Bell />} />
            </IconButton>
          </NavbarTooltip>
        </Badge>

        <MenuTrigger>
          <NavbarAvatarAnchor>
            <NavbarTooltip title={userDisplayName}>
              <NavbarAvatarContainer navBarOrientation={orientation}>
                <Avatar
                  aria-haspopup
                  alt={userDisplayName}
                  rounded
                  css={{ backgroundColor: 'white', size: '$9', fontSize: '$xs' }}
                />
              </NavbarAvatarContainer>
            </NavbarTooltip>
          </NavbarAvatarAnchor>

          <Menu>
            <MenuList>
              <MenuTitle>
                {userDisplayName}
                <Text css={{ mt: 4 }} as="div" variant="metadata" size="xs" textTransform="none">
                  v{appVersion.version}
                </Text>
              </MenuTitle>

              <MenuLink to="/account">Profile</MenuLink>
              <MenuItem onClick={onPreferences}>Preferences</MenuItem>

              <Divider gutter />

              <MenuItem onClick={signOut}>Sign out</MenuItem>
            </MenuList>
          </Menu>
        </MenuTrigger>
      </Flex>

      {children}
    </Navbar>
  );
}

function NavbarTooltip(props: TooltipProps) {
  return <Tooltip placement="auto" enterDelay={500} {...props} />;
}
